// Private Yoga Sessions in Frome - 1-to-1 personalised yoga
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, User, Target, Clock, Check, Shield } from "lucide-react";
import Navigation from "../components/shared/Navigation";
import Footer from "../components/shared/Footer";
import PageBreadcrumb from "../components/shared/PageBreadcrumb";
import { usePageMeta, generateServiceSchema, generateFAQSchema, SchemaScript } from "../components/shared/SEOHelper";

const PrivateYogaPage = () => {
  usePageMeta({
    title: "Private Yoga Sessions Frome | 1-to-1 Yoga Somerset",
    description: "Private one-to-one yoga sessions in Frome, Somerset. Personalised yoga tailored to your body, goals, and pace.",
    canonical: "https://annelielily.com/yoga-classes/private-yoga"
  });

  const serviceSchema = generateServiceSchema({
    name: "Private Yoga Sessions in Frome",
    description: "One-to-one personalised yoga sessions in Frome, Somerset. Tailored practice for your specific needs, goals, and body.",
    serviceType: "Private Yoga Session",
    url: "https://annelielily.com/yoga-classes/private-yoga"
  });

  const faqs = [
    {
      question: "How much do private yoga sessions cost?",
      answer: "Private sessions are £50 for 60 minutes. I also offer packages if you want to commit to regular sessions — get in touch and we can discuss what works for you."
    },
    {
      question: "Where do private sessions take place?",
      answer: "At my studio space in Little Keyford Lane, Frome. It's a calm, private environment with everything we need. Home visits may be possible for mobility issues — just ask."
    },
    {
      question: "What should I wear / bring?",
      answer: "Comfortable clothes you can move in. I provide mats and any props we need. Just bring yourself and any information about injuries or health conditions."
    },
    {
      question: "How many sessions will I need?",
      answer: "That depends on your goals. Some people come for a single session to learn basics before joining a group class. Others prefer ongoing private practice. We can discuss what makes sense for you."
    }
  ];

  const faqSchema = generateFAQSchema(faqs);

  const reasons = [
    {
      title: "You're completely new to yoga",
      desc: "And want to learn the foundations properly before joining a group class. One or two private sessions can give you confidence."
    },
    {
      title: "You have an injury or health condition",
      desc: "Back pain, joint issues, pregnancy, post-surgery recovery — we can build a practice around what your body needs right now."
    },
    {
      title: "You want to deepen your practice",
      desc: "Already practise yoga but want focused work on specific poses, breathwork, or meditation? Private sessions let us go deeper."
    },
    {
      title: "You simply prefer one-to-one",
      desc: "Some people don't enjoy group classes. If you prefer privacy, individual attention, and a practice designed entirely for you."
    },
    {
      title: "Your schedule doesn't fit group classes",
      desc: "If class times don't work for you, private sessions can be scheduled to suit your availability."
    },
    {
      title: "You're preparing for something specific",
      desc: "A marathon, a wedding, a stressful period at work — we can design a practice to support whatever's ahead."
    }
  ];

  return (
    <div className="min-h-screen bg-cream">
      <SchemaScript schema={serviceSchema} />
      <SchemaScript schema={faqSchema} />
      <Navigation />
      <PageBreadcrumb currentPage="Private Yoga" currentSlug="yoga-classes/private-yoga" />
      
      {/* Hero */}
      <section className="pt-8 pb-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-gold font-semibold mb-3 block">
              One-to-One Yoga
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-light text-neutral-800 mb-5">
              Private Yoga Sessions
            </h1>
            <p className="text-neutral-700 leading-relaxed max-w-2xl mx-auto mb-8">
              Yoga designed entirely around you. Your body, your goals, your pace. 
              No distractions, no compromises — just focused attention on what you need.
            </p>
            
            <div className="flex flex-wrap justify-center gap-4 mb-8">
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <User className="w-4 h-4 text-gold" />
                Just you and me
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Clock className="w-4 h-4 text-gold" />
                60 minute sessions
              </span>
              <span className="flex items-center gap-2 bg-white/70 px-4 py-2 rounded-full text-sm text-neutral-700">
                <Target className="w-4 h-4 text-gold" />
                Tailored to you
              </span>
            </div>
            
            <Link to="/contact" className="btn-primary">
              Arrange a Session <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>
      
      {/* What Makes Private Different */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="aspect-square rounded-2xl overflow-hidden">
                <img 
                  src="https://customer-assets.emergentagent.com/job_e5a39abe-98a9-41c1-8b03-8654ab8086e3/artifacts/smt0sdkg_IMG_1963.jpeg"
                  alt="Annelie Lily's private yoga studio in Frome with yoga mats, bolsters and aerial silks in a cosy converted loft space"
                  className="w-full h-full object-contain bg-muted"
                  width={600}
                  height={600}
                  loading="eager"
                  decoding="async"
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
                A Personalised Approach
              </h2>
              <div className="prose-content space-y-4 text-neutral-700">
                <p>
                  In a private session, the practice is designed entirely around you. We can 
                  focus on specific areas you'd like to work on, address any injuries or 
                  limitations, and move at a pace that feels right for your body.
                </p>
                <p>
                  Each session is tailored to your needs that day. If something isn't working, 
                  we adjust. If you'd like to spend longer on a particular area, we can.
                </p>
                <p>
                  It's a supportive space to explore your practice with individual guidance 
                  and attention. If you'd prefer group classes, I also offer <Link to="/yoga-classes/hatha-yoga" className="text-gold hover:underline">Hatha yoga</Link> and <Link to="/yoga-classes/yin-yoga" className="text-gold hover:underline">Yin yoga</Link> in 
                  small groups.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who It's For */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Private Sessions Are Ideal For
            </h2>
          </motion.div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {reasons.map((reason, index) => (
              <motion.div
                key={reason.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-white/70 p-6 rounded-xl border border-gold/10"
              >
                <h3 className="font-heading text-lg text-neutral-800 mb-2">{reason.title}</h3>
                <p className="text-neutral-600 text-sm leading-relaxed">{reason.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* What Happens in a Session */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-6">
              What Happens in a Session
            </h2>
            <div className="prose-content space-y-4 text-neutral-700">
              <p>
                <strong>First session:</strong> We start with a conversation. I want to 
                understand your body, any injuries or limitations, what you're hoping to 
                get from yoga, and how you're feeling today. Then we practice — gently at 
                first, adjusting as we go.
              </p>
              <p>
                <strong>Ongoing sessions:</strong> Each session builds on the last, but 
                also responds to where you are that day. Some days we'll work harder; 
                other days we'll focus on rest and release. You get what you need.
              </p>
              <p>
                <strong>Between sessions:</strong> If useful, I can give you a simple 
                practice to do at home. Nothing overwhelming — just enough to maintain 
                what we're building.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="bg-white/80 p-8 md:p-10 rounded-2xl border border-gold/10 text-center"
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-2">
              Private Session
            </h2>
            <p className="text-gold text-3xl font-heading mb-4">£50</p>
            <p className="text-neutral-600 mb-6">60 minutes, fully tailored to you</p>
            
            <ul className="text-left max-w-md mx-auto space-y-3 mb-8">
              {[
                "Personalised practice for your body",
                "Work on specific goals or challenges",
                "Learn at your own pace",
                "Props and equipment provided",
                "Flexible scheduling"
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-sm text-neutral-700">
                  <Check className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                  {item}
                </li>
              ))}
            </ul>
            
            <Link to="/contact" className="btn-primary">
              Arrange a Session <ArrowRight className="w-4 h-4 inline ml-2" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 px-6 md:px-12 bg-cream">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-8 text-center">
              Common Questions
            </h2>
            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div key={index} className="bg-white/70 p-5 rounded-xl border border-gold/10">
                  <h3 className="font-heading text-lg text-neutral-800 mb-2">{faq.question}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{faq.answer}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-16 px-6 md:px-12 bg-muted">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-2xl md:text-3xl text-neutral-800 mb-4">
              Ready to Start?
            </h2>
            <p className="text-neutral-600 mb-6 max-w-xl mx-auto">
              Get in touch to arrange your first session. No commitment beyond that — 
              see how it feels and take it from there.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="btn-primary">
                Arrange a Session <ArrowRight className="w-4 h-4 inline ml-2" />
              </Link>
              <a href="tel:07540995021" className="btn-secondary">
                Call 07540 995021
              </a>
            </div>
          </motion.div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default PrivateYogaPage;
